// src/components/LiveWanPanel.jsx
//
// The "current state" control panel: WAN priority + mark important (WAN
// and clients), live. No date picker - this is deliberately NOT the
// historical /monitoring/:serialNumber page, which stays untouched.
//
// Reuses WanTable.jsx / ClientTable.jsx / ImportanceToggle.jsx exactly as
// they are - same components the old Dashboard uses, same endpoints,
// same behavior. Nothing here duplicates their internals.
//
// SCOPE: Fully Managed Peplinks only for now. WAN Managed / Data Center
// devices use a different data path in the old Dashboard that depends on
// its large aggregated cache rather than standalone REST endpoints -
// wiring that in without the cache needs its own investigation, so this
// panel says so plainly rather than guessing.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ImportanceToggle from './ImportanceToggle';
import '../styles/Modal.css'; // toggle-switch / slider / knob styling for ImportanceToggle - was missing entirely
import { fetchAllWanFM, fetchAllClientsFM, fetchAllWanManaged, fetchAllClientsManaged, changeWanPriority } from '../utils/routerLiveData';
import './LiveWanPanel.css';
import { saveTextFile } from '../utils/saveFile';
import { apiFetch } from '../api/apiClient';
import {
  ALLOW_DC_PRIORITY_CHANGE,
  isOfflineStatus,
  toDisplayPriority,
  writeRawToDisplay,
  displayToReadRaw,
  priorityLabel,
  loadPendingPriorities,
  savePendingPriorities,
  recordPendingPriorities,
  applyPendingPriorities,
  payloadWanId,
  checkPriorityChange,
} from '../utils/wanPriority';

// Priority logic (conversion, payload, checks, DC switch) lives in
// ../utils/wanPriority.js - shared with the Monitoring page.

// Desktop drag-and-drop only works in real desktop browsers. Inside the
// native Capacitor app (incl. the iOS app running on a Mac - "Designed for
// iPad" / Mac Catalyst) the webview is WKWebView, where a mouse drag never
// fires HTML5 dragstart/drop. There the desktop grid uses the same
// tap-to-set picker as mobile instead. Uses the window.Capacitor global the
// native shell injects, so no @capacitor/core import is needed here.
// Set FORCE_PRIORITY_PICKER = true to test the picker mode in a browser.
const FORCE_PRIORITY_PICKER = false;
function isNativeAppShell() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch {
    return false;
  }
}

// Collapses rows that share a MAC down to one representative row (the
// first one seen), keeping any row that has no MAC at all untouched.
// Used to turn a list of network-presence rows into a list of distinct
// physical devices - see the important-clients derivation below for why
// this matters here specifically.
function dedupeByMac(list) {
  const seen = new Set();
  const result = [];
  for (const item of list) {
    const mac = (item.mac || '').toLowerCase();
    if (!mac) {
      result.push(item);
      continue;
    }
    if (seen.has(mac)) continue;
    seen.add(mac);
    result.push(item);
  }
  return result;
}

// Formats uptime string into a shortened version with specified number of units
// Example: function_x("30 days 20 hours 1 minutes 35 seconds", 2) => "30d 20h"
// Example: function_x("21 hours 1 minutes 35 seconds", 2) => "21h 1m"
function formatUptime(uptimeStr, numUnits) {
  if (!uptimeStr) return '—';
  if (numUnits < 1) return '—';
  
  // Parse the uptime string
  const units = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  };
  
  // Match patterns like "30 days", "20 hours", "1 minutes", "35 seconds"
  const patterns = [
    { regex: /(\d+)\s*days?/, unit: 'days' },
    { regex: /(\d+)\s*hours?/, unit: 'hours' },
    { regex: /(\d+)\s*minutes?/, unit: 'minutes' },
    { regex: /(\d+)\s*seconds?/, unit: 'seconds' }
  ];
  
  let hasMatch = false;
  for (const pattern of patterns) {
    const match = uptimeStr.match(pattern.regex);
    if (match) {
      units[pattern.unit] = parseInt(match[1], 10);
      hasMatch = true;
    }
  }
  
  // If no matches found, return original string
  if (!hasMatch) return uptimeStr;
  
  // Build the result with the specified number of units
  const unitOrder = ['days', 'hours', 'minutes', 'seconds'];
  const unitLabels = {
    days: 'd',
    hours: 'h',
    minutes: 'm',
    seconds: 's'
  };
  
  const parts = [];
  let count = 0;
  
  for (const unit of unitOrder) {
    if (units[unit] > 0) {
      parts.push(`${units[unit]}${unitLabels[unit]}`);
      count++;
      if (count >= numUnits) break;
    }
  }
  
  // If no parts were added (all zero), return '0s' or similar
  if (parts.length === 0) return '0s';
  
  return parts.join(' ');
}

export default function LiveWanPanel({ device, initialMode = 'wan', onClose, canEdit = false, scope = 'admin' }) {
  const navigate = useNavigate();
  const scannerId = device.device_key;
  const managementType = device.peplinkManagementType;
  const isKnownType = ['fully_managed', 'wan_managed', 'data_center'].includes(managementType);

  // Priority changes are blocked entirely for Data Center devices - this is
  // deliberately separate from canEdit (which still governs the
  // important/star toggle) so a DC device stays editable for importance
  // marking while priority changes are hard-disabled. This is enforced in
  // two places: here (hides/disables the UI affordance) and again inside
  // handlePriorityChange itself (refuses even if some other path calls it),
  // so no single missed spot can let a DC priority change through.
  //
  // Routers in a read-only InControl organisation (we are not org admin
  // there) can be monitored but not reconfigured - the backend reports that
  // as can_change_priority=false, and refuses the change too.
  const [ic2Writable, setIc2Writable] = useState(false);
  useEffect(() => {
    if (!canEdit || !scannerId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/monitoring/management-type?serial_number=${encodeURIComponent(scannerId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setIc2Writable(data.can_change_priority === true);
      } catch (err) {
        console.error('[LiveWanPanel] permission check failed', err); // stays false -> priority hidden
      }
    })();
    return () => { cancelled = true; };
  }, [canEdit, scannerId]);
  const canChangePriority = canEdit && ic2Writable && (managementType !== 'data_center' || ALLOW_DC_PRIORITY_CHANGE);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wanData, setWanData] = useState([]);
  const [clients, setClients] = useState([]);
  const [importantWan, setImportantWan] = useState([]);
  const [importantClients, setImportantClients] = useState([]);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [wanFilter, setWanFilter] = useState('all'); // 'all' | 'important' | 'offline_important'
  const [clientFilter, setClientFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');
  const [wanDetail, setWanDetail] = useState({ starlink_by_interface: {}, cellular_by_interface: {} });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState(initialMode); // 'wan' | 'clients' - never both at once
  const [desktopDetailName, setDesktopDetailName] = useState(null);

  useEffect(() => {
    setViewMode(initialMode);
  }, [initialMode]);
  const [priorityPickerWan, setPriorityPickerWan] = useState(null);
  const desktopDragSupported = !FORCE_PRIORITY_PICKER && !isNativeAppShell();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const refetch = useCallback(async () => {
    if (!isKnownType) {
      setLoading(false);
      return;
    }

    setError(null);
    try {
      const isFM = managementType === 'fully_managed';
      const [wanResult, clientResult] = await Promise.all([
        isFM ? fetchAllWanFM(scannerId, device.name, scope) : fetchAllWanManaged(scannerId, scope),
        isFM ? fetchAllClientsFM(scannerId, scope) : fetchAllClientsManaged(scannerId, scope),
      ]);

      const wanId = (wan) => {
        if (managementType !== 'fully_managed') return wan.id;
        const idStr = String(wan.id).toLowerCase();
        const match = idStr.match(/^primary_(\d+)$/);
        if (match) return parseInt(match[1], 10);
        const n = Number(wan.id);
        return Number.isFinite(n) ? n : wan.id;
      };
      setWanData(applyPendingPriorities(scannerId, wanResult.wanConnections, wanId, isFM));
      setImportantWan(wanResult.importantItems);
      setDeviceInfo(wanResult.deviceInfo);
      setClients(clientResult.clients);
      setImportantClients(clientResult.importantItems);
      // Both FM and WM/DC now derive wanDetail from the same live call
      // made above (fetchAllWanFM / fetchAllWanManaged), not the old
      // file-based endpoint - fixes the FM info-icon gap the same way
      // the WM/DC one was fixed earlier.
      setWanDetail(wanResult.wanDetail || { starlink_by_interface: {}, cellular_by_interface: {} });
    } catch (e) {
      setError(e.message || 'Failed to load live device data');
    } finally {
      setLoading(false);
    }
  }, [scannerId, isKnownType, managementType, device.name]);

  useEffect(() => {
    setLoading(true);
    refetch();
  }, [refetch]);

  // WanTable's ImportanceToggle calls window.refreshDashboard() directly
  // (a hardcoded global, not a prop) - same pattern the old Dashboard.jsx
  // uses. We point it at this panel's own refetch while it's mounted, and
  // restore whatever was there before on unmount so we don't leak state
  // if the old Dashboard is ever open in the same session.
  useEffect(() => {
    const previous = window.refreshDashboard;
    window.refreshDashboard = refetch;
    return () => { window.refreshDashboard = previous; };
  }, [refetch]);

  const isFullyManaged = managementType === 'fully_managed';

  // Single entry point for ALL priority changes (desktop drag + mobile
  // picker). Callers pass WHAT they want changed (one WAN, one new display
  // priority); the payload is built here from the FULL, unfiltered wanData
  // so a filtered view can never produce a partial payload.
  //
  // Order of checks - nothing is sent, recorded or shown optimistically
  // until ALL of them pass:
  //   1. Data Center hard block
  //   2. No-op check
  //   3. Payload validation (full coverage, valid values, only the target changes)
  //   4. "Keep at least one connected WAN enabled" safety net
  const handlePriorityChange = async (targetWan, newDisplayPriority) => {
    if (!ic2Writable) {
      alert('This router is read-only in InControl, so its WAN priority cannot be changed from here.');
      return false;
    }
    // All checks (DC block, secondary, no-op, payload validation, safety
    // net) live in wanPriority.checkPriorityChange - shared with the
    // Monitoring page so both screens run the exact same code.
    const check = checkPriorityChange(wanData, targetWan, newDisplayPriority, managementType);
    if (check.status === 'noop') return true;
    if (check.status !== 'ok') {
      if (check.validationError) console.error('[LiveWanPanel] Priority payload refused:', check.validationError, check.payload);
      alert(check.message);
      return false;
    }
    const priorityPayload = check.payload;

    // Optimistic update - only the target actually changes (guaranteed by
    // validation), but applying the whole payload keeps state and pending
    // memory consistent with exactly what was sent.
    const previousWanData = wanData;
    recordPendingPriorities(scannerId, priorityPayload);
    setWanData((prev) =>
      prev.map((wan) => {
        const id = payloadWanId(wan, isFullyManaged);
        if (id === null || id === undefined) return wan;
        const match = priorityPayload.find((p) => String(p.wan_id) === String(id));
        if (!match) return wan;
        // write-side -> display -> this device's read-side convention
        return { ...wan, priority: displayToReadRaw(writeRawToDisplay(match.priority), isFullyManaged) };
      })
    );

    try {
      await changeWanPriority(scannerId, priorityPayload);
      await refetch();
      return true;
    } catch (e) {
      // Revert the optimistic update and drop the now-invalid pending
      // entries so a failed change doesn't keep showing as if it worked.
      setWanData(previousWanData);
      const pending = loadPendingPriorities(scannerId);
      for (const { wan_id } of priorityPayload) delete pending[String(wan_id)];
      savePendingPriorities(scannerId, pending);
      alert(e.message || 'Failed to update WAN priorities');
      return false;
    }
  };

  // Mobile picker: sets ONE WAN's priority, others keep their current
  // priority (converted properly - see buildPriorityPayload).
  const handleMobilePriorityChange = async (targetWan, newDisplayPriority) => {
    setPriorityPickerWan(null);
    await handlePriorityChange(targetWan, newDisplayPriority);
  };

  // Pure client-side filtering - _is_important is already computed by
  // routerLiveData.js, no extra network calls needed for these views.
  const importantWanRows = wanData.filter((w) => w._is_important);
  const offlineImportantWanRows = importantWanRows.filter((w) => isOfflineStatus(w.status || w.status_text));
  const visibleWan =
    wanFilter === 'important' ? importantWanRows :
    wanFilter === 'offline_important' ? offlineImportantWanRows :
    wanData;

  // De-dupe by MAC. A physical client bridged onto more than one VLAN/
  // subnet (confirmed on this fleet: e.g. iconserver appears 5x, TrueNAS
  // 2x, one Cydome unit 7x across different IPs/VLANs in /devices/all)
  // shows up as multiple separate rows in `clients`, and routerLiveData.js
  // correctly flags every one of those rows _is_important - each row IS
  // that device. But that means filtering clients by `_is_important`
  // counts network *presences*, not devices, so the count balloons past
  // /devices/important's per-device count (48 became 52 here: +1 each for
  // 88:9C:AD:18:66:48, 08:7B:87:4B:4D:B9, F0:4A:02:55:F5:47's extra VLAN
  // rows). Collapsing to one representative row per MAC here - after
  // filtering, so the "All" tab still shows every VLAN presence
  // untouched - makes the Important tab's count and rows match
  // /devices/important exactly.
  const importantClientRows = dedupeByMac(clients.filter((c) => c._is_important));
  const offlineImportantClientRows = importantClientRows.filter((c) => (c.status || '').toLowerCase() === 'offline');
  const visibleClients =
    clientFilter === 'important' ? importantClientRows :
    clientFilter === 'offline_important' ? offlineImportantClientRows :
    clients;
  // What's on screen right now (filter pill + search box) - shared by the
  // table/cards and the CSV export so the file always matches the view.
  const shownClients = clientSearch ? visibleClients.filter(matchesClientSearch(clientSearch)) : visibleClients;

  let content;

  if (!isKnownType) {
    content = (
      <div className="lwp-panel">
        <div className="lwp-notice">
          Couldn't determine this device's management type - live controls
          can't be shown safely without knowing which data path to use.
        </div>
        <button className="lwp-history-btn" onClick={() => navigate(`/monitoring/${scannerId}`)}>
          View full history →
        </button>
      </div>
    );
  } else if (loading) {
    content = <div className="lwp-panel lwp-loading">Loading live data…</div>;
  } else if (error) {
    content = (
      <div className="lwp-panel">
        <div className="lwp-error">{error}</div>
        <button className="lwp-retry-btn" onClick={refetch}>Retry</button>
      </div>
    );
  } else {
  content = (
    <div className="lwp-panel">
      {viewMode === 'wan' && (
      <div className="lwp-section">
        <div className="lwp-filter-row">
          <FilterPills
            value={wanFilter}
            onChange={setWanFilter}
            counts={{
              all: wanData.length,
              important: importantWanRows.length,
              offline_important: offlineImportantWanRows.length,
            }}
          />
          {!isMobile && canChangePriority && (
            <span className="lwp-drag-hint">{desktopDragSupported ? 'DRAG TO REORDER PRIORITY' : 'CLICK ⇅ TO SET PRIORITY'}</span>
          )}
        </div>
        {isMobile ? (
          <WanMobileList
            wanData={visibleWan}
            importantWan={importantWan}
            isFullyManaged={isFullyManaged}
            managementType={managementType}
            scannerId={scannerId}
            wanDetail={wanDetail}
            canEdit={canEdit}
            canChangePriority={canChangePriority}
            onPriorityTap={(wan) => setPriorityPickerWan(wan)}
            onToggleSuccess={refetch}
          />
        ) : (
          <DesktopWanGrid
            wanData={visibleWan}
            allWanData={wanData}
            isFullyManaged={isFullyManaged}
            managementType={managementType}
            scannerId={scannerId}
            canEdit={canEdit}
            canChangePriority={canChangePriority}
            wanDetail={wanDetail}
            onShowDetail={(name) => setDesktopDetailName(name)}
            onToggleSuccess={refetch}
            onPriorityChange={handlePriorityChange}
            dragSupported={desktopDragSupported}
            onPriorityTap={(wan) => setPriorityPickerWan(wan)}
          />
        )}
      </div>
      )}

      {viewMode === 'clients' && (
      <div className="lwp-section">
        <div className="lwp-filter-row">
          <FilterPills
            value={clientFilter}
            onChange={setClientFilter}
            counts={{
              all: clients.length,
              important: importantClientRows.length,
              offline_important: offlineImportantClientRows.length,
            }}
          />
          <ClientSearchBox value={clientSearch} onChange={setClientSearch} />
          <button
            type="button"
            className="lwp-csv-btn"
            onClick={() => downloadClientsCsv(shownClients, device.name || scannerId)}
            disabled={shownClients.length === 0}
            title="Download the clients shown below as CSV"
          >
            <i className="fas fa-download"></i> CSV
          </button>

        </div>
        {isMobile ? (
          <ClientMobileList
            clients={shownClients}
            importantClients={importantClients}
            managementType={managementType}
            scannerId={scannerId}
            canEdit={canEdit}
            onToggleSuccess={refetch}
          />
        ) : (
          <DesktopClientGrid
            clients={shownClients}
            managementType={managementType}
            scannerId={scannerId}
            canEdit={canEdit}
            onToggleSuccess={refetch}
          />
        )}
      </div>
      )}

      {priorityPickerWan && (
        <PriorityPickerSheet
          wan={priorityPickerWan}
          wanData={visibleWan}
          isFullyManaged={isFullyManaged}
          device={device}
          onSelect={(newDisplayPriority) => handleMobilePriorityChange(priorityPickerWan, newDisplayPriority)}
          onClose={() => setPriorityPickerWan(null)}
        />
      )}

      {desktopDetailName && (
        <WanDetailModal
          name={desktopDetailName}
          starlinkInfo={wanDetail.starlink_by_interface?.[desktopDetailName]}
          cellularInfo={wanDetail.cellular_by_interface?.[desktopDetailName]}
          onClose={() => setDesktopDetailName(null)}
        />
      )}
    </div>
  );
  }

  return (
    <div className="lwp-modal-overlay" onClick={onClose}>
      <div className="lwp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="lwp-modal-header">
          <span className="lwp-modal-title">{device.name}{device.organization_name ? ` · ${device.organization_name}` : ''}</span>
          <span className={'lwp-online-badge' + (device.online ? '' : ' offline')}>
            {device.online ? `ONLINE${device.uptime ? ' ' + formatUptime(device.uptime, 2) : ''}` : 'OFFLINE'}
          </span>
          <button className="lwp-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="lwp-view-tabs">
          <button
            className={'lwp-view-tab' + (viewMode === 'wan' ? ' active' : '')}
            onClick={() => setViewMode('wan')}
          >
            WAN CONNECTIONS {wanData.length}
          </button>
          <button
            className={'lwp-view-tab' + (viewMode === 'clients' ? ' active' : '')}
            onClick={() => setViewMode('clients')}
          >
            CLIENTS {clients.length}
          </button>
        </div>

        <div className="lwp-modal-scroll">
          {content}
        </div>
      </div>
    </div>
  );
}

function FilterPills({ value, onChange, counts }) {
  const tabs = [
    { id: 'all', label: 'ALL', count: counts.all },
    { id: 'important', label: 'IMPORTANT', count: counts.important },
    { id: 'offline_important', label: 'OFFLINE IMPORTANT', count: counts.offline_important },
  ];

  return (
    <div className="lwp-filter-pills">
      {tabs.map((t) => (
        <button
          key={t.id}
          className={'lwp-filter-pill' + (value === t.id ? ' active' : '')}
          onClick={() => onChange(t.id)}
        >
          {t.label} {t.count}
        </button>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Client CSV export - same fields and same GB maths as DesktopClientGrid.
// ----------------------------------------------------------------------

async function downloadClientsCsv(clients, deviceName) {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const gb = (bytes) => (toNum(bytes) / 1024 ** 3).toFixed(2);

  // Quote every cell, double any quotes inside. A leading = + - @ is
  // prefixed with ' so Excel doesn't run a client's hostname as a formula.
  const cell = (v) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    return '"' + s.replace(/"/g, '""') + '"';
  };

  const header = ['Client Name', 'Manufacturer', 'IP Address', 'MAC Address', 'VLAN',
    'Download (GB)', 'Upload (GB)', 'Total (GB)', 'Status', 'Important'];

  const rows = clients.map((c) => {
    const down = toNum(c.rx) / 1024 ** 3;
    const up = toNum(c.tx) / 1024 ** 3;
    return [
      c.hostname || c.client_name || '',
      c.manufacturer || c.vendor || '',
      c.ip || c.ip_address || '',
      c.mac || '',
      c.vlan != null && c.vlan !== '' ? c.vlan : '',
      gb(c.rx),
      gb(c.tx),
      (down + up).toFixed(2),
      c.status || '',
      c._is_important ? 'Yes' : 'No',
    ];
  });

  const csv = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');

  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const safeName = String(deviceName || 'device').replace(/[^\w.-]+/g, '_');

  // Browser: normal download. Capacitor app: cache file + share sheet.
  // BOM so Excel opens accented hostnames/vendors as UTF-8.
  try {
    await saveTextFile('\uFEFF' + csv, `${safeName}_clients_${stamp}.csv`, {
      dialogTitle: 'Export clients',
    });
  } catch (e) {
    console.error('Client CSV export failed:', e);
    alert('Could not export the CSV. Please try again.');
  }
}

function ClientSearchBox({ value, onChange }) {
  return (
    <div className="lwp-search-box">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1.6">
        <circle cx="7" cy="7" r="4.6"></circle>
        <path d="M10.4 10.4L14 14"></path>
      </svg>
      <input
        type="text"
        placeholder="name · IP · MAC · vendor"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function matchesClientSearch(query) {
  const q = query.trim().toLowerCase();
  return (c) => {
    if (!q) return true;
    const haystack = [
      c.hostname, c.client_name, c.mac, c.ip, c.ip_address, c.manufacturer, c.vendor,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };
}

// ----------------------------------------------------------------------
// Priority grouping - shared by the desktop grid and the mobile card
// list, so "Priority 1/2/3/Disabled" means the same thing in both.
// ----------------------------------------------------------------------

function groupWansByPriority(wanData, isFullyManaged) {
  const buckets = { 0: [], 1: [], 2: [], 3: [], disabled: [] };
  wanData.forEach((wan) => {
    const dp = toDisplayPriority(wan.priority, isFullyManaged);
    const key = dp === 999 ? 'disabled' : Math.min(dp, 3);
    buckets[key].push(wan);
  });
  return [0, 1, 2, 3, 'disabled']
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({ key: k, displayPriority: k === 'disabled' ? 999 : k, items: buckets[k] }));
}

// Which priority slots the desktop grid shows as drop targets - same rule
// as legacy WanTable.getPriorityGroupsToShow():
//   highest used priority + 1 empty slot below it, capped at P4.
//   P1 only        -> P1, P2
//   P1 + P2        -> P1, P2, P3
//   P1 + P3        -> P1, P2, P3, P4   (gaps are always shown)
//   P1 + P4        -> P1, P2, P3, P4
//   P2 only        -> P1, P2, P3       (empty P1 still shown)
//   all disabled   -> P1
// Computed from the FULL WAN list so a filtered view doesn't hide slots.
function priorityDropSlots(allWans, isFullyManaged) {
  let maxDP = -1;
  for (const wan of allWans) {
    const dp = toDisplayPriority(wan.priority, isFullyManaged);
    if (dp !== 999) maxDP = Math.max(maxDP, Math.min(dp, 3));
  }
  const count = Math.min(maxDP + 2, 4); // maxDP -1 -> 1 slot
  return Array.from({ length: count }, (_, i) => i);
}

function usageGB(wan) {
  if (wan.download !== undefined && wan.upload !== undefined) {
    return { down: (wan.download || 0) / 1024, up: (wan.upload || 0) / 1024 };
  }
  if (wan.rx !== undefined && wan.tx !== undefined) {
    return { down: (wan.rx || 0) / (1024 ** 3), up: (wan.tx || 0) / (1024 ** 3) };
  }
  return { down: 0, up: 0 };
}

// ----------------------------------------------------------------------
// Desktop WAN grid - matches mockup 3g exactly: 10-column grid, priority
// group header bars with a status summary, drag-to-reorder, unused
// (disabled) ports collapsed behind "Show all" by default.
// ----------------------------------------------------------------------

const WAN_GRID_COLUMNS = '28px 1.5fr 90px 150px 170px 150px 90px 90px 100px 70px';

function DesktopWanGrid({ wanData, allWanData, isFullyManaged, managementType, scannerId, canEdit, canChangePriority, wanDetail, onShowDetail, onToggleSuccess, onPriorityChange, dragSupported = true, onPriorityTap }) {
  const [showAll, setShowAll] = useState(false);
  const [draggedWan, setDraggedWan] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const starlinkDetail = wanDetail?.starlink_by_interface || {};
  const cellularDetail = wanDetail?.cellular_by_interface || {};

  const groups = groupWansByPriority(wanData, isFullyManaged);
  const disabledGroup = groups.find((g) => g.key === 'disabled');
  // Rows come from the visible (filtered) list; the slots themselves come
  // from the full list. When priority can't be changed, only non-empty
  // groups are shown (nothing to drop into, so empty slots are just noise).
  // Empty slots / Disabled drop box are drag targets - only shown when
  // dragging actually works (not in picker mode).
  const showDropTargets = canChangePriority && dragSupported;
  const activeGroups = showDropTargets
    ? priorityDropSlots(allWanData || wanData, isFullyManaged).map((dp) => {
        const g = groups.find((x) => x.key === dp);
        return g || { key: dp, displayPriority: dp, items: [] };
      })
    : groups.filter((g) => g.key !== 'disabled');
  const disabledCount = disabledGroup ? disabledGroup.items.length : 0;
  const shownCount = wanData.length - (showAll ? 0 : disabledCount);

  const totals = wanData.reduce(
    (acc, w) => {
      const u = usageGB(w);
      return { down: acc.down + u.down, up: acc.up + u.up };
    },
    { down: 0, up: 0 }
  );
  const totalGB = totals.down + totals.up;
  const topWan = [...wanData].sort((a, b) => {
    const au = usageGB(a); const bu = usageGB(b);
    return (bu.down + bu.up) - (au.down + au.up);
  })[0];
  const topShare = topWan && totalGB > 0 ? (((usageGB(topWan).down + usageGB(topWan).up) / totalGB) * 100) : 0;

  const handleDragStart = (wan, e) => {
    const isSecondary = isFullyManaged && String(wan.id).toLowerCase().startsWith('secondary');
    if (isSecondary) { e?.preventDefault(); return; } // Cannot reorder secondary router WAN interfaces - matches legacy WanTable.handleDragStart
    // Firefox won't start/complete an HTML5 drag without data set - same as legacy WanTable.
    try { e?.dataTransfer?.setData('text/plain', String(wan.name || wan.interface_name || '')); } catch { /* ignore */ }
    if (e?.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    setDraggedWan(wan);
  };
  const handleDragEnd = () => { setDraggedWan(null); setDragOverKey(null); };
  const handleDrop = async (targetGroupKey) => {
    if (!draggedWan) return;
    const target = draggedWan;
    const newDisplayPriority = targetGroupKey === 'disabled' ? 999 : targetGroupKey;
    setDraggedWan(null);
    setDragOverKey(null);
    // The parent builds the payload from the FULL wanData - this grid only
    // receives the filtered (visible) rows, so it must never build it here.
    await onPriorityChange(target, newDisplayPriority);
  };

  // Drop-target handlers, shared by group headers and empty-slot rows.
  const dropZoneProps = (key) => ({
    onDragOver: (e) => { if (!draggedWan) return; e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== key) setDragOverKey(key); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverKey(null); },
    onDrop: (e) => { e.preventDefault(); handleDrop(key); },
  });

  // Placeholder row for an empty priority slot - gives the empty group a
  // real area to drop onto, not just a thin header.
  const renderEmptySlot = (key, text) => (
    <div
      className={'lwp-wgrid-empty-slot' + (dragOverKey === key ? ' drag-over' : '')}
      style={{
        padding: '10px 14px',
        margin: '2px 0 6px',
        border: `1px dashed ${dragOverKey === key ? 'var(--color-secondary)' : 'rgba(255,255,255,.2)'}`,
        borderRadius: 6,
        color: 'rgba(255,255,255,.45)',
        fontSize: 12,
        letterSpacing: '.04em',
        textAlign: 'center',
        background: dragOverKey === key ? 'rgba(255,255,255,.05)' : 'transparent',
      }}
      {...dropZoneProps(key)}
    >
      {text}
    </div>
  );

  const renderRow = (wan, groupKey) => {
    const iname = wan.name || wan.interface_name;
    const status = wan.status || wan.status_text || 'unknown';
    const offline = isOfflineStatus(status);
    const standby = status.toLowerCase().includes('standby');
    const { down, up } = usageGB(wan);
    const hasDetail = !!(starlinkDetail[iname] || cellularDetail[iname]);
    const isSecondary = isFullyManaged && String(wan.id).toLowerCase().startsWith('secondary');
    const canDrag = canChangePriority && !isSecondary && dragSupported;
    const canTap = canChangePriority && !isSecondary && !dragSupported;
    const barColor = groupKey === 'disabled' ? 'rgba(255,255,255,.35)' : offline ? 'rgba(255,255,255,.35)' : standby ? 'var(--color-accent)' : 'var(--color-secondary)';

    return (
      <div
        key={iname}
        className={'lwp-wgrid-row' + (draggedWan && dragOverKey === groupKey ? ' drag-over' : '')}
        style={{
          gridTemplateColumns: WAN_GRID_COLUMNS,
          borderLeft: `3px solid ${barColor}`,
          cursor: canChangePriority && isSecondary ? 'not-allowed' : undefined,
          // Whole target group lights up while hovering any of its rows.
          background: draggedWan && dragOverKey === groupKey ? 'rgba(255,255,255,.06)' : undefined,
          opacity: draggedWan && draggedWan === wan ? 0.5 : undefined,
        }}
        draggable={canDrag}
        onDragStart={(e) => handleDragStart(wan, e)}
        onDragEnd={handleDragEnd}
        /* Every row is also a drop target for ITS OWN group: dropping a WAN
           onto a P2 row sets it to P2, exactly like dropping on the P2
           header. Previously only headers / empty slots accepted drops, so
           releasing over a row silently did nothing. */
        {...(showDropTargets ? dropZoneProps(groupKey) : {})}
      >
        <div className="lwp-drag-dots" title={isSecondary ? 'Secondary router WAN - priority cannot be changed' : undefined}>
          {canTap ? (
            <button
              type="button"
              onClick={() => onPriorityTap && onPriorityTap(wan)}
              title="Set priority"
              aria-label={`Set priority for ${iname}`}
              style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'inherit', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
            >
              ⇅
            </button>
          ) : canChangePriority ? (isSecondary ? <i className="fas fa-lock text-muted" /> : '⠿') : ''}
        </div>
        <div className="lwp-wgrid-name">
          <span className={'lwp-dot' + (offline ? '' : standby ? ' standby' : ' online')} />
          <span className="lwp-wgrid-name-text">{iname}</span>
          {hasDetail && (
            <button className="lwp-info-btn" onClick={() => onShowDetail(iname)} title="More details">
              <i className="fas fa-info-circle" />
            </button>
          )}
        </div>
        <div className="lwp-wgrid-role">{isSecondary ? 'SECONDARY' : 'PRIMARY'}</div>
        <div className="lwp-wgrid-mono">{wan.gateway || '—'}</div>
        <div>
          <span className={'lwp-status-chip' + (offline ? ' down' : standby ? ' standby' : ' up')}>
            {status.toUpperCase()}
          </span>
        </div>
        <div className="lwp-wgrid-mono">{wan.ip || wan.ip_address || '—'}</div>
        <div className="lwp-wgrid-num">{down.toFixed(2)}</div>
        <div className="lwp-wgrid-num">{up.toFixed(2)}</div>
        <div className="lwp-wgrid-num strong" style={{ color: offline ? undefined : 'var(--color-secondary)' }}>{(down + up).toFixed(2)}</div>
        <div className="lwp-wgrid-impt">
          {canEdit ? (
            <ImportanceToggle
              deviceType={managementType}
              scannerId={scannerId}
              itemType="wan_interface"
              item={wan}
              isImportant={wan._is_important}
              onToggleSuccess={onToggleSuccess}
            />
          ) : (
            <ReadOnlyStar isImportant={wan._is_important} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="lwp-wgrid">
      <div className="lwp-wgrid-header" style={{ gridTemplateColumns: WAN_GRID_COLUMNS }}>
        <div></div><div>INTERFACE</div><div>ROLE</div><div>GATEWAY</div><div>STATUS</div><div>IP ADDRESS</div>
        <div className="lwp-wgrid-num">↓ GB</div><div className="lwp-wgrid-num">↑ GB</div><div className="lwp-wgrid-num">Σ GB</div><div className="lwp-wgrid-num">IMPT</div>
      </div>

      {activeGroups.map((g) => (
        <React.Fragment key={g.key}>
          <div
            className={'lwp-wgrid-group-head' + (dragOverKey === g.key ? ' drag-over' : '')}
            style={{ borderLeft: `3px solid ${g.displayPriority === 0 ? 'var(--color-secondary)' : g.displayPriority === 1 ? 'var(--color-accent)' : 'rgba(255,255,255,.35)'}` }}
            {...dropZoneProps(g.key)}
          >
            <span className="lwp-wgrid-group-label" style={{ color: g.displayPriority === 0 ? 'var(--color-secondary)' : g.displayPriority === 1 ? 'var(--color-accent)' : 'rgba(255,255,255,.7)' }}>
              {priorityLabel(g.displayPriority).toUpperCase()}
            </span>
          </div>
          {g.items.length > 0
            ? g.items.map((wan) => renderRow(wan, g.key))
            : renderEmptySlot(g.key, draggedWan ? 'Drop here' : 'Empty - drag a WAN here')}
        </React.Fragment>
      ))}

      {/* Disabled group. Its header is ALWAYS a drop target when priority
          can be changed (even with 0 disabled WANs, and even when the rows
          are collapsed) - dropping here sends priority 0 (disabled). The
          "keep one connected WAN" safety net still applies. */}
      {(showDropTargets || (disabledCount > 0 && showAll)) && (
        <React.Fragment>
          <div
            className={'lwp-wgrid-group-head' + (dragOverKey === 'disabled' ? ' drag-over' : '')}
            style={{ borderLeft: '3px solid rgba(255,255,255,.2)' }}
            {...(showDropTargets ? dropZoneProps('disabled') : {})}
          >
            <span className="lwp-wgrid-group-label" style={{ color: 'rgba(255,255,255,.55)' }}>DISABLED</span>
            <span className="lwp-wgrid-group-note">
              {draggedWan ? 'DROP HERE TO DISABLE' : `${disabledCount} · NO LINK${disabledCount > 0 && !showAll ? ' · COLLAPSED' : ''}`}
            </span>
          </div>
          {showAll && disabledGroup && disabledGroup.items.map((wan) => renderRow(wan, 'disabled'))}
          {/* Big drop box - shown WHILE DRAGGING even when disabled WANs exist
              and the section is collapsed, so the target isn't just the thin
              header line. Also shown (idle) when there are no disabled WANs. */}
          {showDropTargets && (draggedWan || disabledCount === 0) &&
            renderEmptySlot('disabled', draggedWan ? 'Drop here to disable' : 'No disabled WANs')}
        </React.Fragment>
      )}

      {disabledCount > 0 && (
        <div className="lwp-wgrid-footer">
          <span className="lwp-wgrid-footer-note">
            {shownCount} OF {wanData.length} SHOWN · {disabledCount} UNUSED PORT{disabledCount !== 1 ? 'S' : ''} {showAll ? 'EXPANDED' : 'COLLAPSED'}
          </span>
          <button className="lwp-wgrid-showall" onClick={() => setShowAll((s) => !s)}>
            {showAll ? 'HIDE' : 'SHOW ALL'}
          </button>
          {totalGB > 0 && (
            <span className="lwp-wgrid-summary">
              Σ {totalGB.toFixed(2)} GB TODAY{topWan ? ` · ${topShare.toFixed(1)}% ON ${topWan.name || topWan.interface_name}` : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Desktop Client grid - matches mockup 3g exactly: 8-column grid + search.
// VLAN column shows the real value when the backend provides one,
// otherwise "—" rather than fabricating a VLAN/role that isn't there.
// ----------------------------------------------------------------------

const CLIENT_GRID_COLUMNS = '1.6fr 140px 170px 110px 90px 90px 100px 70px';

function DesktopClientGrid({ clients, managementType, scannerId, canEdit, onToggleSuccess }) {
  const [sort, setSort] = useState({ key: 'total', dir: 'desc' });

  if (clients.length === 0) {
    return <div className="lwp-mobile-empty">No clients</div>;
  }

  // Number(...) + isFinite guard, not just `|| 0` - a truthy-but-non-numeric
  // rx/tx (bad data from the live feed for one client) would otherwise
  // divide out to NaN. NaN < / > anything is always false, so the
  // comparator below falls through to "equal" against every other row in
  // both directions - a stable sort then never moves that row, leaving it
  // stuck wherever it was in the original (unsorted) array regardless of
  // which column or direction is picked. This is what was reported.
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const usageOf = (c) => ({ down: toNum(c.rx) / 1024 ** 3, up: toNum(c.tx) / 1024 ** 3 });

  const sortValue = (c, key) => {
    switch (key) {
      case 'name': return (c.hostname || c.client_name || c.mac || '').toLowerCase();
      case 'ip': return (c.ip || c.ip_address || '').toLowerCase();
      case 'mac': return (c.mac || '').toLowerCase();
      case 'download': return usageOf(c).down;
      case 'upload': return usageOf(c).up;
      case 'total': { const u = usageOf(c); return u.down + u.up; }
      default: return 0;
    }
  };

  const toggleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key ? (prev.dir === 'asc' ? 'desc' : 'asc') : (key === 'name' || key === 'ip' || key === 'mac' ? 'asc' : 'desc'),
    }));
  };

  const sortedClients = [...clients].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const sortHeader = (key, label, numeric) => (
    <div
      role="button"
      tabIndex={0}
      className={numeric ? 'lwp-wgrid-num' : undefined}
      style={{ cursor: 'pointer', userSelect: 'none', fontWeight: sort.key === key ? 700 : undefined }}
      onClick={() => toggleSort(key)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort(key); }}
    >
      {label}{sort.key === key && (sort.dir === 'asc' ? ' ↑' : ' ↓')}
    </div>
  );

  return (
    <div className="lwp-wgrid">
      <div className="lwp-wgrid-header" style={{ gridTemplateColumns: CLIENT_GRID_COLUMNS }}>
        {sortHeader('name', 'CLIENT')}
        {sortHeader('ip', 'IP ADDRESS')}
        {sortHeader('mac', 'MAC ADDRESS')}
        <div>VLAN</div>
        {sortHeader('download', '↓ GB', true)}
        {sortHeader('upload', '↑ GB', true)}
        {sortHeader('total', 'Σ GB', true)}
        <div className="lwp-wgrid-num">IMPT</div>
      </div>

      {sortedClients.map((c, i) => {
        const rx = toNum(c.rx);
        const tx = toNum(c.tx);
        const down = rx / 1024 ** 3;
        const isOffline = (c.status || '').toLowerCase() === 'offline';
        const name = c.hostname || c.client_name || c.mac || 'Unknown client';
        const isUnknown = !c.hostname && !c.client_name;
        const vlan = c.vlan != null && c.vlan !== '' ? c.vlan : null;

        return (
          <div
            // Composite key, not just `c.mac` - the same physical client
            // can appear on more than one VLAN/subnet (same MAC, different
            // IP), which gave sibling rows an identical key. React can't
            // tell duplicate-keyed siblings apart during reconciliation,
            // so an ImportanceToggle instance (which holds its own local
            // toggle-state) could get reused for the wrong row across
            // renders - this is what made already-unmarked clients
            // (iconserver, TrueNAS, the Cydome unit) intermittently render
            // as if they were in the Important tab. IP is unique per row
            // even when MAC repeats, so mac+ip is a safe unique key.
            key={`${(c.mac || 'nomac').toLowerCase()}-${c.ip || c.ip_address || i}`}
            className="lwp-wgrid-row"
            style={{ gridTemplateColumns: CLIENT_GRID_COLUMNS, borderLeft: `3px solid ${isOffline ? 'rgba(255,255,255,.35)' : 'var(--color-accent)'}` }}
          >
            <div className="lwp-wgrid-client-name">
              <div className="lwp-wgrid-client-row1">
                <span className="lwp-wgrid-name-text">{name}</span>
                {isUnknown && <span className="lwp-status-chip down" style={{ marginLeft: 6 }}>UNKNOWN</span>}
              </div>
              {(c.manufacturer || c.vendor) && <div className="lwp-wgrid-client-sub">{c.manufacturer || c.vendor}</div>}
            </div>
            <div className="lwp-wgrid-mono">{c.ip || c.ip_address || '—'}</div>
            <div className="lwp-wgrid-mono muted">{c.mac || '—'}</div>
            <div className="lwp-wgrid-mono">{vlan != null ? vlan : '—'}</div>
            <div className="lwp-wgrid-num">{down.toFixed(2)}</div>
            <div className="lwp-wgrid-num">{(tx / 1024 ** 3).toFixed(2)}</div>
            <div className="lwp-wgrid-num strong">{(down + tx / 1024 ** 3).toFixed(2)}</div>
            <div className="lwp-wgrid-impt">
              {canEdit ? (
                <ImportanceToggle
                  deviceType={managementType}
                  scannerId={scannerId}
                  itemType="client"
                  item={c}
                  isImportant={c._is_important}
                  onToggleSuccess={onToggleSuccess}
                />
              ) : (
                <ReadOnlyStar isImportant={c._is_important} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatObstruction(stats) {
  if (!stats) return null;
  const pct = stats.fractionObstructed != null ? (stats.fractionObstructed * 100).toFixed(2) + '%' : '—';
  return stats.currentlyObstructed ? `Currently obstructed (${pct} total)` : `Clear (${pct} total)`;
}


// ----------------------------------------------------------------------
// Mobile WAN cards - design reference: RN's screens/RouterDetails/tabs/
// WanTab.js (name/gateway, status dot+badge, color-coded priority badge,
// download/upload/total usage, "Change Priority" opens a picker sheet -
// same interaction as the real app, not invented fresh).
// ----------------------------------------------------------------------

function WanMobileList({ wanData, importantWan, isFullyManaged, managementType, scannerId, wanDetail, canEdit, canChangePriority, onPriorityTap, onToggleSuccess }) {
  const [detailModalName, setDetailModalName] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (wanData.length === 0) {
    return <div className="lwp-mobile-empty">No WAN connections</div>;
  }

  const starlinkDetail = wanDetail?.starlink_by_interface || {};
  const cellularDetail = wanDetail?.cellular_by_interface || {};

  const groups = groupWansByPriority(wanData, isFullyManaged);
  const activeGroups = groups.filter((g) => g.key !== 'disabled');
  const disabledGroup = groups.find((g) => g.key === 'disabled');
  const disabledCount = disabledGroup ? disabledGroup.items.length : 0;

  const totalGB = wanData.reduce((sum, w) => { const u = usageGB(w); return sum + u.down + u.up; }, 0);

const renderCard = (wan) => {
    const wanName = wan.name || wan.interface_name;
    const status = wan.status || wan.status_text || 'unknown';
    const offline = isOfflineStatus(status);
    const standby = status.toLowerCase().includes('standby');
    const { down, up } = usageGB(wan);
    const total = down + up;
    const share = totalGB > 0 ? (total / totalGB) * 100 : 0;
    const isDominant = share >= 50;
    const isSecondary = isFullyManaged && String(wan.id).toLowerCase().startsWith('secondary');
    const hasDetail = !!(starlinkDetail[wanName] || cellularDetail[wanName]);

    return (
      <div
        key={wanName}
        className={'lwp-wan-card' + (isDominant ? ' dominant' : '')}
        style={{ borderLeft: `3px solid ${offline ? 'rgba(255,255,255,.35)' : standby ? 'var(--color-accent)' : 'var(--color-secondary)'}` }}
      >
        <div className="lwp-wan-card-top">
          <span className={'lwp-dot' + (offline ? '' : standby ? ' standby' : ' online')} />
          <div className="lwp-wan-card-name" style={{ flex: 1 }}>{wanName || 'WAN'}</div>
          <span className={'lwp-status-chip sm' + (offline ? ' down' : standby ? ' standby' : ' up')}>
            {offline ? 'DISCONN' : status.toUpperCase()}
          </span>
        </div>

        {/* Meta row now includes the Details button aligned right */}
        <div className="lwp-wan-card-meta indent">
          <span>
            {wan.gateway || wan.ip || wan.ip_address || 'no gateway'} · Σ {total.toFixed(2)} GB
          </span>
          {hasDetail && (
            <button className="lwp-info-btn" onClick={() => setDetailModalName(wanName)}>
              Details
            </button>
          )}
        </div>

        {isDominant && !offline && (
          <>
            <div className="lwp-mini-bar indent"><div style={{ width: `${Math.min(100, share)}%` }} /></div>
            <div className="lwp-wan-card-meta indent accent">Σ {total.toFixed(2)} GB · {share.toFixed(1)}% of today</div>
          </>
        )}

        <div className="lwp-wan-card-actions">
          {canEdit ? (
            <>
              <p className="lwp-important-label">Important</p>
              <ImportanceToggle
                deviceType={managementType}
                scannerId={scannerId}
                itemType="wan_interface"
                item={wan}
                isImportant={wan._is_important}
                onToggleSuccess={onToggleSuccess}
              />
            </>
          ) : (
            <ReadOnlyStar isImportant={wan._is_important} />
          )}
          {canChangePriority && !isSecondary && (
            <button className="lwp-wan-priority-btn sm" onClick={() => onPriorityTap(wan)}>Set Priority</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="lwp-mobile-list">
      {activeGroups.map((g) => (
        <React.Fragment key={g.key}>
          <div
            className="lwp-wgrid-group-head"
            style={{ borderLeft: `3px solid ${g.displayPriority === 0 ? 'var(--color-secondary)' : g.displayPriority === 1 ? 'var(--color-accent)' : 'rgba(255,255,255,.35)'}` }}
          >
            <span className="lwp-wgrid-group-label" style={{ color: g.displayPriority === 0 ? 'var(--color-secondary)' : g.displayPriority === 1 ? 'var(--color-accent)' : 'rgba(255,255,255,.7)' }}>
              {priorityLabel(g.displayPriority).toUpperCase()}
            </span>
          </div>
          {g.items.map(renderCard)}
        </React.Fragment>
      ))}

      {disabledCount > 0 && (
        <>
          <div className="lwp-wgrid-group-head" style={{ borderLeft: '3px solid rgba(255,255,255,.2)' }}>
            <span className="lwp-wgrid-group-label" style={{ color: 'rgba(255,255,255,.55)' }}>UNUSED PORTS</span>
            <span className="lwp-wgrid-group-note">{disabledCount} · NO LINK</span>
          </div>
          {showAll && disabledGroup.items.map(renderCard)}
          {!showAll && (
            <button className="lwp-wgrid-showall block" onClick={() => setShowAll(true)}>SHOW ALL</button>
          )}
        </>
      )}

      {detailModalName && (
        <WanDetailModal
          name={detailModalName}
          starlinkInfo={starlinkDetail[detailModalName]}
          cellularInfo={cellularDetail[detailModalName]}
          onClose={() => setDetailModalName(null)}
        />
      )}
    </div>
  );
}

function WanDetailModal({ name, starlinkInfo, cellularInfo, onClose }) {
  return (
    <div className="lwp-sheet-overlay" onClick={onClose}>
      <div className="lwp-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="lwp-sheet-handle" />
        <div className="lwp-sheet-title">{name}</div>

        {starlinkInfo && (
          <div className="lwp-detail-modal-section">
            <div className="lwp-detail-modal-tag starlink">Starlink</div>
            <div className="lwp-detail-modal-grid">
              {starlinkInfo.kit_serial && (
                <div><span>Kit Serial</span><strong>{starlinkInfo.kit_serial}</strong></div>
              )}
              {starlinkInfo.dish_serial && (
                <div><span>Dish Serial</span><strong>{starlinkInfo.dish_serial}</strong></div>
              )}
              <div><span>Terminal ID</span><strong className="lwp-mono-sm">{starlinkInfo.ut_id || '—'}</strong></div>
              <div><span>Ethernet Speed</span><strong>{starlinkInfo.ethSpeedMbps != null ? `${starlinkInfo.ethSpeedMbps} Mbps` : '—'}</strong></div>
              <div><span>Latency</span><strong>{starlinkInfo.popPingLatencyMs != null ? `${starlinkInfo.popPingLatencyMs} ms` : '—'}</strong></div>
              <div><span>Boresight</span><strong>{starlinkInfo.boresightElevationDeg != null ? `${starlinkInfo.boresightElevationDeg}° / ${starlinkInfo.boresightAzimuthDeg}°` : '—'}</strong></div>
              <div><span>Obstruction</span><strong>{formatObstruction(starlinkInfo.obstruction_stats) || '—'}</strong></div>
            </div>
          </div>
        )}

        {cellularInfo && (
          <div className="lwp-detail-modal-section">
            <div className="lwp-detail-modal-tag cellular">Cellular</div>
            <div className="lwp-detail-modal-grid">
              <div><span>IMEI</span><strong className="lwp-mono-sm">{cellularInfo.imei || '—'}</strong></div>
              <div><span>Carrier</span><strong>{cellularInfo.carrier_name || '—'}</strong></div>
              <div><span>Network</span><strong>{cellularInfo.network || '—'}</strong></div>
              {(cellularInfo.sims || [])[0] && (
                <>
                  <div><span>ICCID</span><strong className="lwp-mono-sm">{cellularInfo.sims[0].iccid || '—'}</strong></div>
                  <div><span>IMSI</span><strong className="lwp-mono-sm">{cellularInfo.sims[0].imsi || '—'}</strong></div>
                </>
              )}
            </div>
          </div>
        )}

        <button className="lwp-sheet-cancel" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function PriorityPickerSheet({ wan, wanData, isFullyManaged, onSelect, onClose }) {
  const currentDP = toDisplayPriority(wan.priority, isFullyManaged);
  const [selected, setSelected] = useState(currentDP);
  const options = [0, 1, 2, 3, 999];

  const groups = groupWansByPriority(wanData, isFullyManaged);
  const countFor = (dp) => {
    const g = groups.find((g) => g.displayPriority === dp);
    return g ? g.items.length : 0;
  };

  const wanName = wan.name || wan.interface_name;
  const status = (wan.status || wan.status_text || 'unknown').toLowerCase();
  const isOffline = isOfflineStatus(status);

  const TITLES = { 0: 'Highest', 1: 'Failover', 2: 'Last resort', 3: 'Emergency', 999: 'Disable this WAN' };
  const DESCRIPTIONS = {
    0: 'carries traffic whenever it is up',
    1: 'used when every P1 is down',
    2: 'only when P1 and P2 are down',
    3: 'metered link, last in the ladder',
    999: 'removed from failover entirely',
  };

  return (
    <div className="lwp-sheet-overlay" onClick={onClose}>
      <div className="lwp-sheet lwp-priority-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="lwp-sheet-handle" />

        <div className="lwp-priority-sheet-header">
          <div className="lwp-priority-sheet-label">MOVE WAN TO</div>
          <div className="lwp-priority-sheet-name">{wanName}</div>
          <div className="lwp-priority-sheet-sub">
            currently P{currentDP === 999 ? '—' : currentDP + 1} · {isOffline ? 'disconnected' : 'connected'}
          </div>
        </div>

        {options.map((p) => {
          const isCurrent = p === currentDP;
          const isSelected = selected === p;
          const count = countFor(p);

          return (
            <button
              key={p}
              type="button"
              className={'lwp-sheet-option' + (isSelected ? ' selected' : '') + (isCurrent ? ' current' : '')}
              onClick={() => setSelected(p)}
            >
              <span className={'lwp-priority-chip' + (isCurrent ? ' current' : '') + (p === 999 ? ' off' : ` p${p}`)}>
                {p === 999 ? 'OFF' : `P${p + 1}`}
              </span>
              <span className="lwp-sheet-option-text">
                <span className="lwp-sheet-option-title">{TITLES[p]}</span>
                <span className="lwp-sheet-option-desc">
                  {DESCRIPTIONS[p]} · {count > 0 ? `${count} WAN${count > 1 ? 's' : ''}` : 'empty'}
                </span>
              </span>
              {isCurrent && <span className="lwp-sheet-option-current">CURRENT</span>}
            </button>
          );
        })}

        <div className="lwp-sheet-footer-row">
          <button type="button" className="lwp-sheet-cancel-btn" onClick={onClose}>CANCEL</button>
          <button
            type="button"
            className="lwp-sheet-apply-btn"
            disabled={selected === currentDP}
            onClick={() => onSelect(selected)}
          >
            Apply change
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Mobile client cards
// ----------------------------------------------------------------------

function ReadOnlyStar({ isImportant }) {
  return (
    <span
      className={'lwp-readonly-star' + (isImportant ? ' important' : '')}
      title={isImportant ? 'Marked important (view only)' : 'Not marked important'}
    >
      {isImportant ? '★' : '☆'}
    </span>
  );
}

function ClientMobileList({ clients, importantClients, managementType, scannerId, canEdit, onToggleSuccess }) {
  // Sort is a single controlled value, not two independent pieces of
  // state - a segment is either the active one (with its direction) or
  // it isn't, which is what the mockup's one-pill-lit-up-at-a-time
  // control shows. Splitting this into separate sortKey/sortDir state
  // (as before) meant a second click on the SAME segment worked, but
  // clicking a segment mid-way through a re-render (e.g. right as
  // clients refetches) could apply to a stale key - collapsing to one
  // state value removes that class of bug entirely.
  const [sort, setSort] = useState({ key: 'total', dir: 'desc' });

  if (clients.length === 0) {
    return <div className="lwp-mobile-empty">No clients</div>;
  }

  const getUsage = (c) => {
    const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
    const rx = toNum(c.rx);
    const tx = toNum(c.tx);
    return { rx, tx, total: rx + tx };
  };

  const sortedClients = [...clients].sort((a, b) => {
    const field = sort.key === 'download' ? 'rx' : sort.key === 'upload' ? 'tx' : 'total';
    const av = getUsage(a)[field];
    const bv = getUsage(b)[field];
    return sort.dir === 'asc' ? av - bv : bv - av;
  });

  const toggleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'desc',
    }));
  };

  const sortSegment = (key, label) => (
    <span
      key={key}
      role="button"
      tabIndex={0}
      className={'lwp-sort-segment' + (sort.key === key ? ' active' : '')}
      onClick={() => toggleSort(key)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort(key); }}
    >
      {label}
      {sort.key === key && <span className="lwp-sort-arrow">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
    </span>
  );

  return (
    <div className="lwp-mobile-list">
      <div className="lwp-sort-row">
        <span className="lwp-sort-row-label">SORT BY</span>
        <div className="lwp-sort-segments">
          {sortSegment('download', 'DOWN')}
          {sortSegment('upload', 'UP')}
          {sortSegment('total', 'TOTAL')}
        </div>
      </div>
      {sortedClients.map((client, i) => {
        const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
        const rx = toNum(client.rx);
        const tx = toNum(client.tx);
        const hasUsage = rx > 0 || tx > 0;
        const isOffline = (client.status || '').toLowerCase() === 'offline';
        const rxGB = rx / 1024 / 1024 / 1024;
        const txGB = tx / 1024 / 1024 / 1024;
        const totalGB = rxGB + txGB;
        const rxPct = totalGB > 0 ? (rxGB / totalGB) * 100 : 0;
        const vlan = client.vlan != null && client.vlan !== '' ? client.vlan : null;
        const name = client.hostname || client.client_name || client.mac || 'Unknown client';

        return (
          <div
            // Composite key - see the desktop table's identical fix above
            // for why `client.mac` alone isn't safe here (multi-VLAN
            // devices repeat the same MAC across rows).
            key={`${(client.mac || 'nomac').toLowerCase()}-${client.ip || client.ip_address || client.hostname || i}`}
            className="lwp-client-card"
            style={{ borderLeft: `3px solid ${isOffline ? '#fff' : 'var(--color-secondary)'}` }}
          >
            <div className="lwp-wan-card-top">
              <span className="lwp-wan-card-name" style={{ flex: 'none' }}>{name}</span>
              {client._is_important && <span className="lwp-client-important-chip">IMPORTANT</span>}
              <span className={'lwp-status-chip' + (isOffline ? '' : ' up') + ' lwp-client-status-chip'}>
                {isOffline ? 'OFFLINE' : 'ONLINE'}
              </span>
              {canEdit ? (
                <ImportanceToggle
                  deviceType={managementType}
                  scannerId={scannerId}
                  itemType="client"
                  item={client}
                  isImportant={client._is_important}
                  onToggleSuccess={onToggleSuccess}
                />
              ) : null}
            </div>

            <div className="lwp-wan-card-meta">
              {client.mac || '—'} · {client.ip || client.ip_address || 'N/A'}{vlan != null ? ` · VLAN ${vlan}` : ''}
            </div>

            {hasUsage && (
              <>
                <div className="lwp-wan-card-usage">
                  <span>↓ {rxGB.toFixed(2)} GB</span>
                  <span>↑ {txGB.toFixed(2)} GB</span>
                  <strong>{totalGB.toFixed(2)} GB</strong>
                </div>
                <div className="lwp-mini-bar two-seg">
                  <span style={{ width: `${rxPct}%`, background: 'rgba(136,244,235,.9)' }} />
                  <span style={{ width: `${100 - rxPct}%`, background: 'rgba(136,244,235,.35)' }} />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
